import React, { useState } from "react";
import { validateAddr } from "../utils/addressValidation";

const CheckoutEditModal = ({ address, loading, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    name: address.name || "",
    email: address.email || "",
    address_type: address.address_type || address.type || "Home",
    address: address.address || address.street || "",
    city: address.city || "",
    state: address.state || "",
    pincode: address.pincode || "",
    latitude: address.latitude ?? "",
    longitude: address.longitude ?? "",
    is_default: address.is_default || false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const EDIT_REQUIRED = [
    "name",
    "email",
    "address",
    "city",
    "state",
    "pincode",
    "latitude",
    "longitude",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let clean = value;
    if (name === "pincode") clean = value.replace(/\D/g, "").slice(0, 6);
    else if (name === "city" || name === "state")
      clean = value.replace(/[^a-zA-Z\s]/g, "");
    const final = type === "checkbox" ? checked : clean;
    setForm((p) => ({ ...p, [name]: final }));
    if (touched[name])
      setErrors((p) => ({ ...p, [name]: validateAddr(name, final) }));
  };

  const handleBlur = (e) => {
    setTouched((p) => ({ ...p, [e.target.name]: true }));
    setErrors((p) => ({
      ...p,
      [e.target.name]: validateAddr(e.target.name, e.target.value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    const newTouched = {};
    EDIT_REQUIRED.forEach((f) => {
      newTouched[f] = true;
      newErrors[f] = validateAddr(f, form[f] ?? "");
    });
    setTouched(newTouched);
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;
    onSubmit(form);
  };

  const ic = (name) =>
    `w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 ${
      errors[name] && touched[name]
        ? "border-red-400 focus:ring-red-100"
        : "border-gray-200 focus:border-primary-500 focus:ring-primary-100"
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <h2 className="mb-4 text-lg font-bold text-gray-900">Edit Address</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
                Name *
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("name")}
                placeholder="Rahul Sharma"
              />
              {errors.name && touched.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
                Email *
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("email")}
                placeholder="rahul@email.com"
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
              Address Type
            </label>
            <div className="flex gap-2">
              {["Home", "Work", "Other"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, address_type: t }))}
                  className={`flex-1 rounded-xl border py-2 text-xs font-bold transition ${
                    form.address_type === t
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-gray-200 text-gray-500"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
              Address *
            </label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              onBlur={handleBlur}
              rows={2}
              className={ic("address")}
              placeholder="123, MG Road"
            />
            {errors.address && touched.address && (
              <p className="mt-1 text-xs text-red-500">{errors.address}</p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
                Pincode *
              </label>
              <input
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("pincode")}
                placeholder="411001"
              />
              {errors.pincode && touched.pincode && (
                <p className="mt-1 text-xs text-red-500">{errors.pincode}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
                City *
              </label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("city")}
                placeholder="Pune"
              />
              {errors.city && touched.city && (
                <p className="mt-1 text-xs text-red-500">{errors.city}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
                State *
              </label>
              <input
                name="state"
                value={form.state}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("state")}
                placeholder="Maharashtra"
              />
              {errors.state && touched.state && (
                <p className="mt-1 text-xs text-red-500">{errors.state}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Latitude <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                name="latitude"
                type="number"
                step="any"
                value={form.latitude}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("latitude")}
                placeholder="18.52"
              />
              {errors.latitude && touched.latitude && (
                <p className="mt-1 text-xs text-red-500">{errors.latitude}</p>
              )}
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Longitude <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                name="longitude"
                type="number"
                step="any"
                value={form.longitude}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic("longitude")}
                placeholder="73.85"
              />
              {errors.longitude && touched.longitude && (
                <p className="mt-1 text-xs text-red-500">{errors.longitude}</p>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <input
              name="is_default"
              type="checkbox"
              checked={form.is_default}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            Make default
          </label>
        </div>
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 font-semibold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 rounded-xl bg-primary-600 py-2.5 font-bold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {loading ? "Saving..." : "Update Address"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CheckoutEditModal;
